import { useState } from 'react';
import type { BrowserDialog } from '@br/protocol';
import { Alert, Button, Input, Modal } from '../components/ui';
import { RequestError, type ViewerSocket } from './socket';

export function BrowserDialogModal({
  dialog,
  socket,
  enabled,
  onRequestControl,
  onTakeControl,
}: {
  dialog: BrowserDialog;
  socket: ViewerSocket;
  enabled: boolean;
  onRequestControl?: () => void;
  onTakeControl?: () => void;
}) {
  const [text, setText] = useState(dialog.defaultValue);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const respond = async (accept: boolean) => {
    if (!enabled || busy) return;
    setBusy(true);
    setError('');
    try {
      await socket.request({
        t: 'dialog.respond',
        tabId: dialog.tabId,
        dialogId: dialog.dialogId,
        accept,
        promptText: text,
      });
      socket.dismissClosedDialog(dialog.dialogId);
    } catch (err) {
      if (err instanceof RequestError && err.code === 'not_found') {
        socket.dismissClosedDialog(dialog.dialogId);
        return;
      }
      setError(err instanceof Error ? err.message : '응답을 전달하지 못했습니다');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open
      title={dialog.type === 'prompt' ? '원격 페이지 입력 요청' : '원격 페이지 알림'}
      onClose={() => {
        void respond(dialog.type === 'alert');
      }}
      size="sm"
      footer={
        <>
          {!enabled && onRequestControl && <Button onClick={onRequestControl}>제어권 요청</Button>}
          {!enabled && onTakeControl && (
            <Button onClick={onTakeControl}>관리자 제어권 가져오기</Button>
          )}
          {dialog.type !== 'alert' && (
            <Button disabled={!enabled || busy} onClick={() => void respond(false)}>
              취소
            </Button>
          )}
          <Button
            variant="primary"
            disabled={!enabled || busy}
            loading={busy}
            onClick={() => void respond(true)}
          >
            확인
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="max-h-64 overflow-auto whitespace-pre-wrap break-words text-sm">
          {dialog.message}
        </p>
        {dialog.type === 'prompt' && (
          <Input
            aria-label="응답 내용"
            autoFocus
            value={text}
            maxLength={20000}
            disabled={!enabled || busy}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void respond(true);
            }}
          />
        )}
        {!enabled && <p className="text-sm text-ink-muted">현재 제어자가 응답할 수 있습니다.</p>}
        {error && <Alert level="error">{error}</Alert>}
      </div>
    </Modal>
  );
}
